import { GameConfig, GameState, SceneType, InputState } from '../types/game.types';
import { RenderManager } from './RenderManager';
import { InputManager } from './InputManager';
import { SceneManager } from './SceneManager';
import { AudioManager } from './AudioManager';

export class GameEngine {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private renderManager: RenderManager;
  private inputManager: InputManager;
  private sceneManager: SceneManager;
  private audioManager: AudioManager;
  
  private config: GameConfig;
  private gameState: GameState;
  private lastTime: number = 0;
  private frameId: number = 0;
  private isRunning: boolean = false;

  constructor(canvas: HTMLCanvasElement, config: GameConfig) {
    this.canvas = canvas;
    this.config = config;
    
    // Set up canvas context
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2D rendering context');
    }
    this.ctx = ctx;
    
    // Initialize managers
    this.renderManager = new RenderManager(this.ctx, config);
    this.inputManager = new InputManager();
    this.sceneManager = new SceneManager();
    this.audioManager = new AudioManager();
    
    // Initialize game state
    this.gameState = {
      scene: SceneType.MENU,
      score: 0,
      lives: 3,
      level: 1,
      paused: false,
      gameOver: false
    };
    
    this.setupCanvas();
    this.bindEvents();
  }

  private setupCanvas(): void {
    // Set canvas internal resolution (don't touch if already set by main.ts)
    if (this.canvas.width !== this.config.width || this.canvas.height !== this.config.height) {
      this.canvas.width = this.config.width;
      this.canvas.height = this.config.height;
    }

    // Don't call updateCanvasScale - let main.ts handle sizing

    // Disable image smoothing for pixel-perfect rendering
    this.ctx.imageSmoothingEnabled = false;

    // Set default font
    this.ctx.font = '14px "Courier New", monospace';
    this.ctx.textBaseline = 'top';
  }

  private updateCanvasScale(): void {
    // Don't override canvas size - let main.ts handle responsive sizing
    // This method is kept for compatibility but does nothing
    return;
  }

  private bindEvents(): void {
    // Resize handler
    window.addEventListener('resize', () => {
      this.updateCanvasScale();
    });
    
    // Visibility change handler (pause when tab is hidden)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && !this.gameState.paused) {
        this.pause();
      }
    });
    
    // Prevent context menu on canvas
    this.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }

  public start(): void {
    if (this.isRunning) return;
    
    console.log('Starting C64 Arcade Game Engine');
    this.isRunning = true;
    this.lastTime = performance.now();
    this.frameId = requestAnimationFrame(this.gameLoop);
  }

  public stop(): void {
    if (!this.isRunning) return;
    
    console.log('Stopping C64 Arcade Game Engine');
    this.isRunning = false;
    
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
  }

  public pause(): void {
    this.gameState.paused = true;
    console.log('Game paused - stack trace:', new Error().stack);
  }

  public resume(): void {
    this.gameState.paused = false;
    this.lastTime = performance.now();
    console.log('Game resumed');
  }

  public switchScene(scene: SceneType): void {
    this.gameState.scene = scene;
    this.sceneManager.switchScene(scene);
  }

  private gameLoop = (currentTime: number): void => {
    if (!this.isRunning) return;
    
    const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 1/30); // Cap at 30 FPS minimum
    this.lastTime = currentTime;
    
    // Get current input state (before updating previous keys)
    const inputState = this.inputManager.getInputState();
    
    // Handle global input (for pause/unpause)
    this.handleGlobalInput(inputState);
    
    // Skip scene update if paused
    if (!this.gameState.paused) {
      this.sceneManager.update(deltaTime, inputState);
    }
    
    this.render();
    
    // Update input manager at END of frame (for next frame's edge detection)
    this.inputManager.update();
    
    this.frameId = requestAnimationFrame(this.gameLoop);
  };

  // This method is no longer needed as we moved the logic to gameLoop
  // private update(deltaTime: number): void { }

  private render(): void {
    // Clear canvas
    this.renderManager.clear();
    
    // Render current scene
    this.sceneManager.render(this.ctx);
    
    // Render pause overlay if paused
    if (this.gameState.paused) {
      this.renderPauseOverlay();
    }
    
    // Render mobile controls if on mobile
    const mobileHandler = this.inputManager.getMobileHandler();
    if (mobileHandler && mobileHandler.getIsMobile()) {
      mobileHandler.renderControls(this.ctx);
    }
  }

  private handleGlobalInput(input: InputState): void {
    // Debug logging
    if (input.keys['KeyP']) {
      console.log('P key is pressed, isKeyJustPressed:', this.inputManager.isKeyJustPressed('KeyP'));
    }
    
    // Toggle pause with P key
    if (this.inputManager.isKeyJustPressed('KeyP')) {
      console.log('P key just pressed! Toggling pause. Current paused state:', this.gameState.paused);
      if (this.gameState.paused) {
        this.resume();
      } else {
        this.pause();
      }
    }
    
    // Toggle mute with M key
    if (this.inputManager.isKeyJustPressed('KeyM')) {
      this.audioManager.toggleMute();
    }
    
    // Toggle fullscreen with F key
    if (this.inputManager.isKeyJustPressed('KeyF')) {
      this.toggleFullscreen();
    }
    
    // Return to menu with ESC key
    if (this.inputManager.isKeyJustPressed('Escape')) {
      if (this.gameState.scene !== SceneType.MENU) {
        this.switchScene(SceneType.MENU);
      }
    }
  }

  private renderPauseOverlay(): void {
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    const text = 'PAUSED';
    const textWidth = this.ctx.measureText(text).width;
    const x = (this.config.width - textWidth) / 2;
    const y = this.config.height / 2 - 20;
    
    this.renderManager.drawText(text, x, y, '#62D0FE', '24px "Courier New", monospace');
    
    const subText = 'Press P to resume';
    const subTextWidth = this.ctx.measureText(subText).width;
    const subX = (this.config.width - subTextWidth) / 2;
    
    this.renderManager.drawText(subText, subX, y + 40, '#FFFFFF', '14px "Courier New", monospace');
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.warn);
    } else {
      document.exitFullscreen().catch(console.warn);
    }
  }

  // Getters
  public getGameState(): GameState {
    return { ...this.gameState };
  }

  public getConfig(): GameConfig {
    return { ...this.config };
  }

  public getRenderManager(): RenderManager {
    return this.renderManager;
  }

  public getInputManager(): InputManager {
    return this.inputManager;
  }

  public getSceneManager(): SceneManager {
    return this.sceneManager;
  }

  public getAudioManager(): AudioManager {
    return this.audioManager;
  }
}